import * as THREE from "three";
import "./style.css";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import firefliesVertexShader from "./shaders/fireflies/vertex.glsl";
import firefliesFragmentShader from "./shaders/fireflies/fragment.glsl";
import dustVertexShader from "./shaders/dust/vertex.glsl";
import dustFragmentShader from "./shaders/dust/fragment.glsl";
import { Reflector } from "three/examples/jsm/objects/Reflector.js";


const debugObject = {};

// Loading Manager
const loadingManager = {
  total: 5, // 4 GLTF models + 1 HDRI
  loaded: 0,
  isComplete: false
};

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Sizes
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

// Scene
const scene = new THREE.Scene();

// Off-white background for clean studio look
scene.background = new THREE.Color(0x00000); // White cassé (off-white)

function createMirrorFloor() {
  const geometry = new THREE.PlaneGeometry(60, 60); // taille du sol

  const mirror = new Reflector(geometry, {
    clipBias: 0.003,
    textureWidth: window.innerWidth * window.devicePixelRatio,
    textureHeight: window.innerHeight * window.devicePixelRatio,
    color: 0xffffff,   // Blanc pur pour sol miroir
  });

  mirror.rotation.x = -Math.PI * 0.5; // à plat
  mirror.position.y = 0;              // hauteur du sol

  scene.add(mirror);
}




function createReflectivePlane() {
  const geometryPl = new THREE.PlaneGeometry(200, 200);

  const materialPl = new THREE.MeshStandardMaterial({
    color: 0xf5f5f5,  // blanc léger, pas ultra pur
    metalness: 0.2,
    roughness: 0.3,   // pas trop lisse pour éviter le côté "glace cramée"
  });

  materialPl.envMap = environmentMap;
  materialPl.envMapIntensity = 0.7; // clé pour éviter le cramé
  materialPl.side = THREE.DoubleSide;
  materialPl.needsUpdate = true;

  const plane = new THREE.Mesh(geometryPl, materialPl);
  plane.rotation.x = -Math.PI * 0.5;
  plane.receiveShadow = true;
  scene.add(plane);
}



function applyEnvMapToObject(object3D) {
  if (!environmentMap) return;

  object3D.traverse((child) => {
    if (child.isMesh && child.material) {
      const mat = child.material;

      if (mat.isMeshStandardMaterial || mat.isMeshPhysicalMaterial) {
        mat.envMap = environmentMap;
        mat.envMapIntensity = 1.0;

        if ("metalness" in mat) {
          mat.metalness = 0.8;  // très métallique
          mat.roughness = 0.1;  // reflets nets (pas flous)
        }

        mat.needsUpdate = true;
      }
    }
  });
}

/**
 * Fireflies (Green Particles) - Main system with character halos
 */
const firefliesGeometry = new THREE.BufferGeometry();
const firefliesCount = 2500; // More particles for denser effect
const positionArray = new Float32Array(firefliesCount * 3);
const scaleArray = new Float32Array(firefliesCount);

// Character positions for halo particles (aligned with actual character positions)
const haloCharacterPositions = [
  { x: -12, z: 0 },
  { x: -4, z: 0 },
  { x: 4, z: 0 },
  { x: 12, z: 0 },
];

let particleIndex = 0;

// Add ambient fireflies (scattered around the scene)
for (let i = 0; i < 200; i++) {
  positionArray[particleIndex * 3 + 0] = (Math.random() - 0.5) * 60;
  positionArray[particleIndex * 3 + 1] = Math.random() * 8;
  positionArray[particleIndex * 3 + 2] = (Math.random() - 0.5) * 20;
  scaleArray[particleIndex] = Math.random() * 0.8 + 0.2;
  particleIndex++;
}

// Add dense halo particles around each character
for (let charIdx = 0; charIdx < haloCharacterPositions.length; charIdx++) {
  const charPos = haloCharacterPositions[charIdx];
  const particlesPerChar = 300; // Much denser halos

  for (let i = 0; i < particlesPerChar; i++) {
    // Multiple layers of particles at different radii
    const layer = Math.floor(i / 100);
    const haloRadius = 1.5 + layer * 0.5; // Layers at different distances

    // Random distribution in cylindrical volume around character
    const angle = Math.random() * Math.PI * 2;
    const radiusVariation = Math.random() * haloRadius;
    const heightVariation = Math.random() * 4 - 0.5;

    positionArray[particleIndex * 3 + 0] =
      charPos.x + Math.cos(angle) * radiusVariation;
    positionArray[particleIndex * 3 + 1] = heightVariation;
    positionArray[particleIndex * 3 + 2] =
      charPos.z + Math.sin(angle) * radiusVariation;

    scaleArray[particleIndex] = 0.4 + Math.random() * 0.6;
    particleIndex++;
  }
}

firefliesGeometry.setAttribute(
  "position",
  new THREE.BufferAttribute(positionArray, 3)
);
firefliesGeometry.setAttribute(
  "aScale",
  new THREE.BufferAttribute(scaleArray, 1)
);

const firefliesMaterial = new THREE.ShaderMaterial({
  uniforms: {
    uTime: { value: 0 },
    uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
    uSize: { value: 80 }, // Slightly smaller for more subtle effect
    uParticleColor: { value: new THREE.Color(0x00FF8F) },
  },
  vertexShader: firefliesVertexShader,
  fragmentShader: firefliesFragmentShader,
  transparent: true,
  blending: THREE.AdditiveBlending,
  depthWrite: false,
});

const fireflies = new THREE.Points(firefliesGeometry, firefliesMaterial);
scene.add(fireflies);

/**
 * Atmospheric Dust Particles
 */
const dustGeometry = new THREE.BufferGeometry();
const dustCount = 800; // Dense atmospheric particles
const dustPositionArray = new Float32Array(dustCount * 3);
const dustScaleArray = new Float32Array(dustCount);
const dustRandomnessArray = new Float32Array(dustCount);

// Create particles filling the entire scene volume
for (let i = 0; i < dustCount; i++) {
  // Wide distribution across the scene
  dustPositionArray[i * 3 + 0] = (Math.random() - 0.5) * 80; // X: -40 to 40
  dustPositionArray[i * 3 + 1] = Math.random() * 12; // Y: 0 to 12
  dustPositionArray[i * 3 + 2] = (Math.random() - 0.5) * 40; // Z: -20 to 20

  dustScaleArray[i] = Math.random() * 0.5 + 0.3; // Random scale
  dustRandomnessArray[i] = Math.random();
}

dustGeometry.setAttribute(
  "position",
  new THREE.BufferAttribute(dustPositionArray, 3)
);
dustGeometry.setAttribute(
  "aScale",
  new THREE.BufferAttribute(dustScaleArray, 1)
);
dustGeometry.setAttribute(
  "aRandomness",
  new THREE.BufferAttribute(dustRandomnessArray, 1)
);

const dustMaterial = new THREE.ShaderMaterial({
  uniforms: {
    uTime: { value: 0 },
    uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
    uSize: { value: 30 },
  },
  vertexShader: dustVertexShader,
  fragmentShader: dustFragmentShader,
  transparent: true,
  blending: THREE.NormalBlending,
  depthWrite: false,
});

const dustParticles = new THREE.Points(dustGeometry, dustMaterial);
scene.add(dustParticles);

/**
 * Motion Blur Effect Container
 */
const blurCanvas = document.createElement("canvas");
blurCanvas.style.position = "fixed";
blurCanvas.style.top = "0";
blurCanvas.style.left = "0";
blurCanvas.style.width = "100%";
blurCanvas.style.height = "100%";
blurCanvas.style.pointerEvents = "none";
blurCanvas.style.zIndex = "50";
document.body.appendChild(blurCanvas);

const blurCtx = blurCanvas.getContext("2d");
blurCanvas.width = window.innerWidth;
blurCanvas.height = window.innerHeight;

// Motion blur effect
function drawMotionBlur(intensity) {
  if (intensity > 0) {
    const gradient = blurCtx.createLinearGradient(0, 0, blurCanvas.width, 0);
    gradient.addColorStop(0, `rgba(0, 100, 50, ${intensity * 0.15})`);
    gradient.addColorStop(0.5, "rgba(0, 255, 136, 0)");
    gradient.addColorStop(1, `rgba(0, 100, 50, ${intensity * 0.15})`);

    blurCtx.fillStyle = gradient;
    blurCtx.fillRect(0, 0, blurCanvas.width, blurCanvas.height);
  }
}

const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("/draco/");

const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);

const characters = [];
let currentCharacterIndex = 1; // Start at center card (index 1 for 4 characters)
let isAnimatingCamera = false;

// Character positions in a line (horizontally)
const characterPositions = [
  { x: -12, z: 0 },
  { x: -3.5, z: 0 },
  { x: 4, z: 0, },
  { x: 12.5, z: 0 },
];

// Camera X positions for horizontal movement
const cameraPositions = [-10, -2, 6, 14];

// Model paths for each character (4 models)
const modelPaths = [
  "/models/Fox/glTF/goku-v1.glb",    // Position 1
  "/models/Fox/glTF/robot1-v2.glb",  // Position 0
  "/models/Fox/glTF/zombig-v4.glb",   // Position 2
  "/models/Fox/glTF/antirobo-v1.glb",  // Position 3
];

// Configuration for each character (scale, position offset, animation index)
const characterConfigs = [
  { scale: 2, positionOffset: { x: 0, y: 0, z: 2 }, animationIndex: 1 },
  { scale: 1, positionOffset: { x: 0, y: 0, z: 0 }, animationIndex: 0 },
  { scale: 2.5, positionOffset: { x: 0, y: 0, z: -4 }, animationIndex: 0 },
  { scale: 3, positionOffset: { x: 0, y: 0, z: 0 }, animationIndex: 2 },
];

// Character theme configuration (colors and images)
const characterThemes = [
  {
    name: "GOKU",
    class: "CLASS: SUPER SAIYAN",
    description: "A legendary warrior with immense power and unmatched combat skills.",
    color: { r: 1.0, g: 0.84, b: 0.0 }, // Gold/Yellow
    particleColor: "#FFD700",
    uiBorderColor: "#FFD700",
    textColor: "#FFD700",
    imageUrl: "/uimage4-min.png",
    stats: { power: 95, speed: 88, defense: 72, hp: 85 }
  },
  {
    name: "ROBOT-1",
    class: "CLASS: CYBER-NINJA",
    description: "A rigus synthetic, robust for earth strikes and unassertful agility.",
    color: { r: 0.0, g: 1.0, b: 0.56 }, // Cyan/Green
    particleColor: "#00FF8F",
    uiBorderColor: "#00FF8F",
    textColor: "#00FF8F",
    imageUrl: "/uimage1-min.png",
    stats: { power: 75, speed: 92, defense: 68, hp: 78 }
  },
  {
    name: "ZOMBIG",
    class: "CLASS: UNDEAD WARRIOR",
    description: "A relentless creature of darkness with supernatural strength and resilience.",
    color: { r: 0.5, g: 0.0, b: 0.5 }, // Purple
    particleColor: "#FF0000",
    uiBorderColor: "#FF0000",
    textColor: "#FF0000",
    imageUrl: "/uimage3-min.png",
    stats: { power: 82, speed: 65, defense: 90, hp: 95 }
  },
  {
    name: "ANTIBOT",
    class: "CLASS: TECH DESTROYER",
    description: "Advanced combat unit designed for maximum destruction and tactical superiority.",
    color: { r: 1.0, g: 0.0, b: 0.0 }, // Red
    particleColor: "#8000FF",
    uiBorderColor: "#8000FF",
    textColor: "#8000FF",
    imageUrl: "/uimage2-min.png",
    stats: { power: 98, speed: 70, defense: 85, hp: 88 }
  },
    {
    name: "LOCKED",
    class: "LOCKED",
    description: "...",
    color: { r: 0.0, g: 0.0, b: 0.0 }, // Red
    particleColor: "#ffffffff",
    uiBorderColor: "#ffffffff",
    textColor: "#ffffffff",
    imageUrl: "/robotlock-min.png",
    stats: { power: 0, speed: 0, defense: 0, hp: 0 }
  },
];

// Load multiple character models
for (let i = 0; i < characterPositions.length; i++) {
  const modelPath = modelPaths[i];
  const config = characterConfigs[i];
  
  gltfLoader.load(modelPath, (gltf) => {
    const character = {
      scene: gltf.scene,
      mixer: new THREE.AnimationMixer(gltf.scene),
      animations: gltf.animations,
    };

    // Position character
    gltf.scene.position.x = characterPositions[i].x + config.positionOffset.x;
    gltf.scene.position.y = config.positionOffset.y;
    gltf.scene.position.z = characterPositions[i].z + config.positionOffset.z;

    // Apply scale
    gltf.scene.scale.set(config.scale, config.scale, config.scale);
    applyEnvMapToObject(gltf.scene);

    // Play specific animation based on config
    if (gltf.animations.length > 0) {
      const animIndex = Math.min(config.animationIndex, gltf.animations.length - 1);
      const action = character.mixer.clipAction(gltf.animations[animIndex]);
      action.play();
    }

    scene.add(gltf.scene);
    characters[i] = character;

    // Update loading progress
    loadingManager.loaded++;
    updateLoadingProgress();
  });
}

/**
 * Generate and initialize carousel cards with new structure
 */
function initializeCarouselCards() {
  const carouselTrack = document.getElementById("carouselTrack");

  // Generate cards dynamically
  characterThemes.forEach((theme, index) => {
    const card = document.createElement("div");
    card.className = index === currentCharacterIndex ? "character-card active" : "character-card";
    card.setAttribute("data-index", index);

    card.innerHTML = `
    <img src="${theme.imageUrl}" alt="${theme.name}">
      <div class="card-content">
      </div>
      <div class="card-info">
        <div class="card-title">${theme.name}</div>
        <div class="card-stats-grid">
          <div class="stat-item">
            <span class="stat-label">HP</span>
            <span class="stat-value">${theme.stats.hp}</span>
            <div class="stat-bar">
              <div class="stat-bar-fill" style="width: ${theme.stats.hp}%"></div>
            </div>
          </div>
          <div class="stat-item">
            <span class="stat-label">Attack</span>
            <span class="stat-value">${theme.stats.power}</span>
            <div class="stat-bar">
              <div class="stat-bar-fill" style="width: ${theme.stats.power}%"></div>
            </div>
          </div>
          <div class="stat-item">
            <span class="stat-label">Speed</span>
            <span class="stat-value">${theme.stats.speed}</span>
            <div class="stat-bar">
              <div class="stat-bar-fill" style="width: ${theme.stats.speed}%"></div>
            </div>
          </div>
          <div class="stat-item">
            <span class="stat-label">Defense</span>
            <span class="stat-value">${theme.stats.defense}</span>
            <div class="stat-bar">
              <div class="stat-bar-fill" style="width: ${theme.stats.defense}%"></div>
            </div>
          </div>
        </div>
        <div class="card-icons">
          <div class="card-icon">⚡</div>
          <div class="card-icon">🛡️</div>
          <div class="card-icon">⚔️</div>
        </div>
      </div>
    `;

    carouselTrack.appendChild(card);
    updateCardTransform(card, index);
  });

  // Apply initial theme
  applyCharacterTheme(currentCharacterIndex);
}

/**
 * Update single card transform based on relative position
 */
function updateCardTransform(card, index) {
  // Calculate relative position to current character
  let relativeIndex = index - currentCharacterIndex;
  
  // Handle wraparound
  if (relativeIndex > 2) {
    relativeIndex -= 5;
  } else if (relativeIndex < -2) {
    relativeIndex += 5;
  }
  
  // Apply 3D transform based on relative position
  let translateX = 0;
  let translateZ = 0;
  let rotateY = 0;
  let scale = 1;
  let opacity = 0.6;
  
  if (relativeIndex === 0) {
    // Center card - in front
    translateX = 0;
    translateZ = 0;
    rotateY = 0;
    scale = 1.1;
    opacity = 1;
    card.classList.add("active");
  } else if (relativeIndex === 1 || relativeIndex === -4) {
    // Next card - right side, further back
    translateX = 240;
    translateZ = -200;
    rotateY = 25;
    scale = 0.8;
    opacity = 0.5;
    card.classList.remove("active");
  } else if (relativeIndex === -1 || relativeIndex === 4) {
    // Previous card - left side, further back
    translateX = -240;
    translateZ = -200;
    rotateY = -25;
    scale = 0.8;
    opacity = 0.5;
    card.classList.remove("active");
  } else {
    // Far cards - even further back
    translateX = relativeIndex > 0 ? 480 : -480;
    translateZ = -300;
    rotateY = relativeIndex > 0 ? 40 : -40;
    scale = 0.6;
    opacity = 0.3;
    card.classList.remove("active");
  }
  
  card.style.transform = `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`;
  card.style.opacity = opacity;
}

// Color interpolation state for smooth particle transitions
let currentParticleColor = new THREE.Color(0x00FF8F);
let targetParticleColor = new THREE.Color(0x00FF8F);
let colorTransitionProgress = 1.0;



/**
 * Apply character theme (colors and UI styling)
 */
function applyCharacterTheme(characterIndex, immediate = false) {
  const theme = characterThemes[characterIndex];

  // Set target particle color for smooth transition
  targetParticleColor.setStyle(theme.particleColor);
  colorTransitionProgress = immediate ? 1.0 : 0.0;

  // Update UI border and accent colors for all cards
  const cards = document.querySelectorAll(".character-card");
  cards.forEach((card) => {
    card.style.borderColor = theme.uiBorderColor;
    card.style.boxShadow = `0 0 20px ${theme.uiBorderColor}40, inset 0 0 20px ${theme.uiBorderColor}20`;
  });

  // Update active card styling with gradient background
  const activeCard = document.querySelector(".character-card.active");
  if (activeCard) {
    activeCard.style.boxShadow = `0 0 40px ${theme.uiBorderColor}, inset 0 0 20px ${theme.uiBorderColor}`;
    activeCard.style.borderColor = theme.uiBorderColor;
    activeCard.style.background = `linear-gradient(135deg, ${theme.uiBorderColor}33 0%, ${theme.uiBorderColor}1a 100%)`;
  }

    const hamburger = document.querySelector(".hamburger-menu");
  const bars = document.querySelectorAll(".hamburger-icon .bar");
  const menuLabel = document.querySelector(".menu-label");

  if (hamburger) {
    hamburger.style.borderColor = theme.uiBorderColor;
    hamburger.style.boxShadow = `0 0 20px ${theme.uiBorderColor}66`;
    hamburger.style.background = `rgba(0, 0, 0, 0.6)`; // tu peux changer si tu veux plus de couleur
  }

  if (bars.length) {
    bars.forEach((bar) => {
      bar.style.background = `linear-gradient(90deg, ${theme.particleColor} 0%, ${theme.uiBorderColor} 100%)`;
      bar.style.boxShadow = `0 0 8px ${theme.uiBorderColor}99`;
    });
  }

  if (menuLabel) {
    menuLabel.style.color = theme.textColor;
    menuLabel.style.textShadow = `0 0 10px ${theme.uiBorderColor}99`;
  }



  // Update character name
  const charName = document.querySelector(".character-name");
  if (charName) {
    charName.textContent = theme.name;
    charName.style.color = "#ffffff";
    charName.style.textShadow = `0 0 10px ${theme.uiBorderColor}80`;
  }

  // Update character class
  const charClass = document.querySelector(".character-class");
  if (charClass) {
    charClass.textContent = theme.class;
    charClass.style.color = theme.textColor;
  }
    const charClass1 = document.querySelector(".character-class1");
  if (charClass) {
    // charClass1.textContent = theme.class;
    charClass1.style.color = theme.textColor;
  }


  // Update description text
  const descriptionText = document.querySelector(".description-panel p");
  if (descriptionText) {
    descriptionText.textContent = theme.description;
    descriptionText.style.color = theme.textColor;
  }

  // Update navigation buttons (arrows)
  const navButtons = document.querySelectorAll(".nav-btn");
  navButtons.forEach((btn) => {
    btn.style.color = theme.textColor;
    btn.style.textShadow = `0 0 20px ${theme.uiBorderColor}60`;
  });

  // Update card colors based on their character theme
  const allCards = document.querySelectorAll(".character-card");
  allCards.forEach((card, index) => {
    const cardTheme = characterThemes[index];

    // Update stat bars color for each card
    const statBars = card.querySelectorAll(".stat-bar-fill");
    statBars.forEach((bar) => {
      bar.style.background = `linear-gradient(90deg, ${cardTheme.particleColor} 0%, ${cardTheme.uiBorderColor} 100%)`;
    });

    // Update card class color
    const cardClass = card.querySelector(".card-class");
    if (cardClass) {
      cardClass.style.color = cardTheme.textColor;
    }

    // Update card icons
    const icons = card.querySelectorAll(".card-icon");
    icons.forEach((icon) => {
      icon.style.borderColor = cardTheme.uiBorderColor;
      icon.style.background = `rgba(${cardTheme.color.r * 255}, ${cardTheme.color.g * 255}, ${cardTheme.color.b * 255}, 0.15)`;
    });
  });
}

/**
 * Lights
 */
// const ambientLight = new THREE.AmbientLight(0xffffff, .4);
// scene.add(ambientLight);

/**
 * Camera
 */
let camera;
if (sizes.width < sizes.height) {
   camera = new THREE.PerspectiveCamera(
  45,
  sizes.height / sizes.width,
  0.1,
  100
);
camera.position.set(0, 2.5, 5);

}else{
 camera = new THREE.PerspectiveCamera(
  45,
  sizes.width / sizes.height,
  0.1,
  100
);
camera.position.set(0, 2.5, 5);

}

let currentCameraIndex = 2; // Start at center character

// Mouse tracking for camera rotation
let mouseX = 0;
let mouseY = 0;
let targetRotationX = 0.5;
let targetRotationY = 0.25; // Initial rotation matching right-side mouse
// Initial rotation values to match the right-side mouse position
let currentRotationX = 0.5;
let currentRotationY = 0.25; // Start with rotation to the right

// Set initial camera look-at with right offset
const baseCharacterX = -2; // cameraPositions[currentCameraIndex]
const initialLookY = 1.2 + (currentRotationX * 2);
const initialLookX = baseCharacterX + (currentRotationY * 3);
camera.lookAt(initialLookX, initialLookY, 0);

window.addEventListener("mousemove", (event) => {
  // Normalize mouse position to -1 to 1
  mouseX = (event.clientX / window.innerWidth) * 2 - 1;
  mouseY = -(event.clientY / window.innerHeight) * 2 + 1;

  // Calculate target rotation based on mouse position
  targetRotationY = mouseX * 0.3; // Rotation around Y axis (left-right)
  targetRotationX = mouseY * 0.2; // Rotation around X axis (up-down)
});

/**
 * Window Resize
 */
// window.addEventListener("resize", () => {
//   sizes.width = window.innerWidth;
//   sizes.height = window.innerHeight;

//   camera.aspect = sizes.width / sizes.height;
//   camera.updateProjectionMatrix();

//   renderer.setSize(sizes.width, sizes.height);
//   renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

//   firefliesMaterial.uniforms.uPixelRatio.value = Math.min(
//     window.devicePixelRatio,
//     2
//   );
// });

// Lock screen orientation to landscape
// if (window.screen && window.screen.orientation) {
//   window.screen.orientation.lock('landscape').catch(() => {
//     // Fallback silencieux si le verrouillage échoue
//   });
// }

// // Prevent orientation change
// window.addEventListener('orientationchange', () => {
//   if (Math.abs(window.orientation) !== 90) {
//     document.body.style.transform = 'rotate(90deg)';
//   }
// });

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  antialias: true,
});
if (sizes.width < sizes.height) {
  renderer.setSize(window.innerHeight, window.innerWidth);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
camera.aspect = window.innerHeight / window.innerWidth;

}else{
  renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
camera.aspect = window.innerWidth / window.innerHeight;

}

renderer.setClearColor("#0a0a0a");
renderer.shadowMap.enabled = true;

renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.7;  // baisse si encore trop lumineux
renderer.outputEncoding = THREE.sRGBEncoding; //

const pmremGenerator = new THREE.PMREMGenerator(renderer);
pmremGenerator.compileEquirectangularShader();

let rgbeLoader = new RGBELoader();
let environmentMap = null;

rgbeLoader
    .setPath('/')          // adapte ce chemin
    .load('sky.hdr', (hdrTexture) => { // adapte le nom du fichier
        // conversion HDRI -> envMap
        const envRT = pmremGenerator.fromEquirectangular(hdrTexture);
        environmentMap = envRT.texture;

        scene.environment = environmentMap;   // éclaire tous les MeshStandard
        // scene.background = environmentMap; // optionnel : HDRI en fond

        hdrTexture.dispose();
        pmremGenerator.dispose();

        // Plane réfléchissant une fois l'envMap prête
    createMirrorFloor();


        // Si certains personnages sont déjà chargés, on leur applique aussi
        characters.forEach((character) => {
            if (!character) return;
            applyEnvMapToObject(character.scene);
        });

        // Update loading progress
        loadingManager.loaded++;
        updateLoadingProgress();
    });

/**
 * UI Interaction
 */
const carouselTrack = document.getElementById("carouselTrack");
const prevBtn = document.getElementById("prevBtn");
const nextBtn = document.getElementById("nextBtn");

function updateCarousel(direction) {
  if (isAnimatingCamera) return;
    if (characters.length === 0) {
    return;
  }

  isAnimatingCamera = true;

  // Update current index
  if (direction === "next") {
    currentCharacterIndex = (currentCharacterIndex + 1) % characters.length;
  } else {
    currentCharacterIndex =
      (currentCharacterIndex - 1 + characters.length) % characters.length;
  }

  // Camera horizontal movement with smooth easing
  const startX = camera.position.x;
  const endX = cameraPositions[currentCharacterIndex];

  const duration = 0.6; // seconds - faster movement (was 1.0)
  const startTime = Date.now();

  const cameraAnimationInterval = setInterval(() => {
    const elapsed = (Date.now() - startTime) / 1000;
    const progress = Math.min(elapsed / duration, 1);

    // Snappier easing function (ease-out-back for slight overshoot)
    const easeProgress = progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;

    // Motion blur intensity based on progress
    const blurIntensity = Math.sin(progress * Math.PI) * 0.8; // Increased blur intensity
    drawMotionBlur(blurIntensity);

    // Move camera horizontally with smooth interpolation
    const newX = startX + (endX - startX) * easeProgress;
    camera.position.set(newX, 2.5, 5);
    camera.lookAt(newX, 1.2, 0);

    if (progress >= 1) {
      clearInterval(cameraAnimationInterval);
      isAnimatingCamera = false;
      blurCtx.clearRect(0, 0, blurCanvas.width, blurCanvas.height);
      // Reset camera rotation after animation
      targetRotationX = 0;
      targetRotationY = 0;
      currentRotationX = 0;
      currentRotationY = 0;
    }
  }, 16);

  // Update carousel cards with 3D depth effect
  const cards = document.querySelectorAll(".character-card");
  cards.forEach((card, index) => {
    updateCardTransform(card, index);
  });

  // Update UI theme based on current character with smooth transition
  applyCharacterTheme(currentCharacterIndex, false);
}

prevBtn.addEventListener("click", () => updateCarousel("prev"));
nextBtn.addEventListener("click", () => updateCarousel("next"));

/**
 * Animation Loop
 */
let previousTime = 0;
const clock = new THREE.Clock();


// function resize() {

//   const camera = new THREE.PerspectiveCamera(
//   45,
//   sizes.height / sizes.width,
//   0.1,
//   100
// );

// renderer.setSize(window.innerHeight, window.innerWidth);
// camera.aspect = window.innerHeight / window.innerWidth;


//   camera.updateProjectionMatrix();
//   console.log('yes');
  
// }

// resize();

/**
 * Loading UI Functions
 */
function updateLoadingProgress() {
  const loadingBarFill = document.getElementById('loadingBarFill');
  const loadingPercentage = document.getElementById('loadingPercentage');
  const enterBtn = document.getElementById('enterBtn');
  
  // Calculate percentage
  const percentage = Math.round((loadingManager.loaded / loadingManager.total) * 100);
  
  if (loadingBarFill) {
    loadingBarFill.style.width = `${percentage}%`;
  }
  
  if (loadingPercentage) {
    loadingPercentage.textContent = `${percentage}%`;
  }
  
  // Check if all assets are loaded
  if (loadingManager.loaded >= loadingManager.total && !loadingManager.isComplete) {
    loadingManager.isComplete = true;
    if (enterBtn) {
      enterBtn.disabled = false;
    }
  }
}

function hideLoadingScreen() {
  const loadingScreen = document.getElementById('loadingScreen');
  
  if (loadingScreen) {
    // Add fade-out class
    loadingScreen.classList.add('fade-out');
    
    // Remove loading screen after animation
    setTimeout(() => {
      loadingScreen.remove();
    }, 800);
  }
  
  // Initialize carousel after loading screen is hidden
  initializeCarouselCards();
  
  // Start animation loop
  animate();
}

// Add enter button event listener
const enterBtn = document.getElementById('enterBtn');
if (enterBtn) {
  enterBtn.addEventListener('click', hideLoadingScreen);
}

function animate() {
  const elapsedTime = clock.getElapsedTime();
  const deltaTime = elapsedTime - previousTime;
  previousTime = elapsedTime;

  // Smooth particle color transition
  if (colorTransitionProgress < 1.0) {
    colorTransitionProgress = Math.min(colorTransitionProgress + deltaTime * 1.2, 1.0);

    // Interpolate between current and target color
    currentParticleColor.lerpColors(
      currentParticleColor,
      targetParticleColor,
      deltaTime * 2.5
    );

    // Update shader uniform with interpolated color
    firefliesMaterial.uniforms.uParticleColor.value.copy(currentParticleColor);
  }

  // Apply smooth camera rotation following mouse ONLY if not animating carousel
  if (!isAnimatingCamera) {
    // Smooth interpolation of rotation
    currentRotationX += (targetRotationX - currentRotationX) * 0.1;
    currentRotationY += (targetRotationY - currentRotationY) * 0.1;

    // Update camera look-at based on rotation
    // Calculate the look-at point with rotation applied
    const baseCharacterX = cameraPositions[currentCharacterIndex];
    const lookY = 1.2 + (currentRotationX * 2);
    const lookX = baseCharacterX + (currentRotationY * 3);
    const lookZ = 0;
    camera.lookAt(lookX, lookY, lookZ);
  }

  // Update all character animations
  for (let i = 0; i < characters.length; i++) {
    if (characters[i]) {
      characters[i].mixer.update(deltaTime);
    }
  }

  // Update fireflies
  firefliesMaterial.uniforms.uTime.value = elapsedTime;

  // Update dust particles
  dustMaterial.uniforms.uTime.value = elapsedTime;

  // Clear motion blur each frame
  blurCtx.clearRect(0, 0, blurCanvas.width, blurCanvas.height);

  // Render
  renderer.render(scene, camera);
  window.requestAnimationFrame(animate);
}

// Animation loop will be started from hideLoadingScreen() after user clicks enter
// animate();
